/**
 * Eligibility + frequency semantics for a PatientProcedureCoverage row.
 *
 * Lifted verbatim from Breakdown/BreakdownProceduresTable.js so the on-screen
 * table (icon + tooltip) and the printed breakdown document read a row the same
 * way. Behaviour is the table's — nothing here was re-derived. If a rule needs
 * to change, change it here and both surfaces follow.
 */

// One of five eligibility states (+ two "don't know" states). Prefers the
// structured `eligible_today_bool` from the breakdown LLM extraction, falling
// back to keyword-matching the legacy `eligible_today` text for rows that
// haven't been re-run since the bool was introduced.
export function classifyEligibility(record) {
  const text = record && record.eligible_today != null ? String(record.eligible_today) : "";
  const lowerText = text.toLowerCase();
  const bool = record ? record.eligible_today_bool : undefined;
  const coverage = record && record.percentage_covered != null
    ? Number(record.percentage_covered)
    : null;

  // No structured bool AND no text → never been checked.
  if ((bool === null || bool === undefined) && !text) return "unchecked";

  // Hard-no signals override everything.
  if (lowerText.includes("procedure code not found")) return "not_found";
  if (lowerText.includes("no coverage") && !lowerText.includes("today")) return "not_covered";

  // Structured signal first: false = not eligible right now.
  if (bool === false) {
    // BUT — if we know the procedure has real coverage (e.g., 100%), it's a
    // temporary cooldown, not a permanent "not covered". This is the case the
    // old text-only icon got wrong (red ✕ for what's really an amber 🕒).
    if (coverage !== null && coverage > 0) return "cooldown";
    return "not_eligible";
  }
  if (bool === true) return "eligible";

  // Fallback to legacy text parsing for rows without the new bool.
  if (lowerText.includes("not eligible")) {
    if (coverage !== null && coverage > 0) return "cooldown";
    return "not_eligible";
  }
  if (lowerText.includes("eligible")) return "eligible";

  return "unclear";
}

export const ELIGIBILITY_LABEL = {
  eligible:     "Eligible today",
  cooldown:     "Covered, in cooldown",
  not_eligible: "Not eligible today",
  not_covered:  "Not covered",
  not_found:    "Not found at carrier",
  unchecked:    "Not yet checked",
  unclear:      "Eligibility unclear",
};

// "every 12 months" — the tooltip's wording for a frequency window. 999 is the
// "unlimited" sentinel and yields null (there is no window to describe).
export function humanizeFrequencyWindow(months) {
  if (months == null || months === 999) return null;
  const m = Number(months);
  if (!isFinite(m)) return null;
  return `every ${m} months`;
}

// The Frequency column's text: "2 every 12 months", "Unlimited", a bare
// occurrence count when no window is recorded, or "-".
export function formatFrequency(record) {
  const text = record ? record.max_occurrences : undefined;
  return record && record.max_occurrences && !record.coverage_duration_months
    ? String(record.max_occurrences)
    : record && record.coverage_duration_months === 999
      ? "Unlimited"
      : text
        ? `${text} every ${record.coverage_duration_months} months`
        : "-";
}

// "today" / "yesterday" / "N days ago", or null when the row carries no usable
// last_verified date. Data-age, so a just-checked row reads differently from a
// stale one.
export function lastVerifiedAge(record, now = Date.now()) {
  if (!record || !record.last_verified) return null;
  const d = new Date(record.last_verified);
  if (isNaN(d.getTime())) return null;
  const days = Math.floor((now - d.getTime()) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}
